import { useCallback, useState } from 'react';

/**
 * Coupled-state hook for a from/to date pair.
 *
 * Behaviour (matches a classic booking-site picker):
 *   - When `from` changes and both ends were already set, `to` follows by
 *     the same delta — duration is preserved.
 *   - When `from` is picked for the first time and `to` is still empty,
 *     `to` is left empty for filter ranges (minSpan 0) and auto-set to
 *     `from + minSpan` for booking ranges (minSpan ≥ 1) so the form is
 *     immediately submittable.
 *   - When the partial state has `to` set but `from` empty, picking `from`
 *     snaps `to` forward if it would otherwise violate minSpan.
 *   - `to` changes never shift `from` — moving the end is a deliberate
 *     "narrow / extend" gesture.
 *
 * The pure transitions `applySetFrom` / `applySetTo` are exported so the
 * coupling logic is testable in a node environment without jsdom.
 */
export type DateRangeOptions = {
  initialFrom?: string;
  initialTo?: string;
  /**
   * Minimum days the end must lie after the start.
   * - `0` (default) for inclusive filter ranges where from = to is valid.
   * - `1` for booking ranges where check_out is exclusive of check_in.
   */
  minSpanDays?: number;
};

export type DateRangeInputBindings = {
  value: string;
  onChange: (next: string) => void;
  min?: string;
  max?: string;
};

export type DateRange = {
  from: string;
  to: string;
  setFrom: (next: string) => void;
  setTo: (next: string) => void;
  fromInput: DateRangeInputBindings;
  toInput: DateRangeInputBindings;
  /** Imperative reset (e.g. when the BulkActions inputs follow the visible
   *  month). Bypasses the coupling so both ends can be set in one shot. */
  reset: (from: string, to: string) => void;
};

/** Add `n` calendar days to a YYYY-MM-DD string, returning the same format.
 *  UTC-anchored arithmetic side-steps DST jumps. */
export function addDays(yyyymmdd: string, n: number): string {
  if (!yyyymmdd) return yyyymmdd;
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10);
}

/** Days from `a` to `b` (b - a). Negative when b < a. */
export function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round(
    (Date.UTC(by!, bm! - 1, bd!) - Date.UTC(ay!, am! - 1, ad!)) / 86_400_000,
  );
}

type State = { from: string; to: string };

/** Pure transition for setFrom — see hook docs. */
export function applySetFrom(prev: State, next: string, minSpan: number): State {
  if (!next) return { from: '', to: prev.to };
  if (prev.from && prev.to) {
    const delta = daysBetween(prev.from, next);
    return { from: next, to: delta === 0 ? prev.to : addDays(prev.to, delta) };
  }
  if (prev.to) {
    return {
      from: next,
      to: daysBetween(next, prev.to) < minSpan ? addDays(next, minSpan) : prev.to,
    };
  }
  if (minSpan >= 1) return { from: next, to: addDays(next, minSpan) };
  return { from: next, to: '' };
}

/** Pure transition for setTo — see hook docs. */
export function applySetTo(prev: State, next: string, minSpan: number): State {
  if (!next) return { from: prev.from, to: '' };
  if (prev.from && daysBetween(prev.from, next) < minSpan) {
    return { from: prev.from, to: addDays(prev.from, minSpan) };
  }
  return { from: prev.from, to: next };
}

export function useDateRange(opts: DateRangeOptions = {}): DateRange {
  const minSpan = opts.minSpanDays ?? 0;
  const [from, setFromState] = useState(opts.initialFrom ?? '');
  const [to, setToState] = useState(opts.initialTo ?? '');

  const setFrom = useCallback(
    (next: string) => {
      const out = applySetFrom({ from, to }, next, minSpan);
      setFromState(out.from);
      setToState(out.to);
    },
    [from, to, minSpan],
  );

  const setTo = useCallback(
    (next: string) => {
      const out = applySetTo({ from, to }, next, minSpan);
      setFromState(out.from);
      setToState(out.to);
    },
    [from, to, minSpan],
  );

  const reset = useCallback((nextFrom: string, nextTo: string) => {
    setFromState(nextFrom);
    setToState(nextTo);
  }, []);

  // Native picker constraints. `from` must be ≤ `to - minSpan`; `to` must be
  // ≥ `from + minSpan`. Undefined when the counterpart is empty.
  const fromMax = to ? addDays(to, -minSpan) : undefined;
  const toMin = from ? addDays(from, minSpan) : undefined;

  return {
    from,
    to,
    setFrom,
    setTo,
    fromInput: { value: from, onChange: setFrom, max: fromMax },
    toInput: { value: to, onChange: setTo, min: toMin },
    reset,
  };
}
